/**
 * AutoCompleteURL Field Renderer
 *
 * URL-based autocomplete with API fetching
 * Fetches suggestions from a remote API endpoint as user types
 * Supports debouncing, minimum character count, and custom rendering
 */

import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import * as LucideIcons from 'lucide-react';
import type { FieldRenderer } from '@/MyCode/core';
import type { FormField } from '@/MyCode/components/JsonFormSteps';

/**
 * AutoCompleteURL Component
 * Handles state and API fetching for autocomplete functionality
 */
const AutoCompleteURLComponent: React.FC<{
    field: FormField;
    value: any;
    handleChange: (name: string, value: any) => void;
    isRequired: boolean;
}> = ({ field, value, handleChange, isRequired }) => {
    const [searchQuery, setSearchQuery] = React.useState('');
    const [suggestions, setSuggestions] = React.useState<any[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [showDropdown, setShowDropdown] = React.useState(false);
    const [selectedItem, setSelectedItem] = React.useState<any>(null);

    const {
        url,
        valueColumn = 'id',
        primaryColumn = 'title',
        secondaryColumn = 'description',
        search = 'q',
        minChars = 2, // Minimum characters to trigger search (renamed from triggerWordsCount)
        debounce = 300,
        dataPath = 'products', // Path to array in response (e.g., 'products' for response.products)
        renderItem, // Optional custom render function
    } = field;

    const dropdownRef = React.useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Debounced search
    React.useEffect(() => {
        // Check minimum character count instead of word count
        if (searchQuery.trim().length < minChars) {
            setSuggestions([]);
            return;
        }

        const timer = setTimeout(async () => {
            setIsLoading(true);
            try {
                const searchUrl = `${url}${url.includes('?') ? '&' : '?'}${search}=${encodeURIComponent(searchQuery)}`;
                const response = await fetch(searchUrl, {
                    credentials: 'include',
                });
                const data = await response.json();

                // Extract array from response using dataPath
                const items = dataPath ? data[dataPath] : data;
                setSuggestions(Array.isArray(items) ? items : []);
                setShowDropdown(true);
            } catch (error) {
                console.error('Autocomplete fetch error:', error);
                setSuggestions([]);
            } finally {
                setIsLoading(false);
            }
        }, debounce);

        return () => clearTimeout(timer);
    }, [searchQuery, url, search, debounce, minChars, dataPath]);

    const handleSelect = (item: any) => {
        setSelectedItem(item);
        setSearchQuery(item[primaryColumn] || '');
        handleChange(field.name, item[valueColumn]);
        setShowDropdown(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        if (!e.target.value) {
            setSelectedItem(null);
            handleChange(field.name, '');
        }
    };

    const SearchIcon = LucideIcons.Search;
    const LoaderIcon = LucideIcons.Loader2;

    return (
        <div className="space-y-2" ref={dropdownRef}>
            <Label>
                {field.label}
                {isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>
            {field.description && (
                <p className="text-sm text-muted-foreground">{field.description}</p>
            )}
            <div className="relative">
                <div className="relative">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                    <Input
                        type="text"
                        value={searchQuery}
                        onChange={handleInputChange}
                        placeholder={field.placeholder || 'Start typing to search...'}
                        className="pl-10"
                        onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
                    />
                    {isLoading && (
                        <LoaderIcon className="absolute right-3 top-1/2 -translate-y-1/2 size-4 animate-spin text-muted-foreground" />
                    )}
                </div>

                {showDropdown && suggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-60 overflow-auto">
                        {suggestions.map((item, index) => {
                            const itemValue = item[valueColumn];
                            const primary = item[primaryColumn];
                            const secondary = secondaryColumn ? item[secondaryColumn] : null;

                            return (
                                <button
                                    key={itemValue || index}
                                    type="button"
                                    onClick={() => handleSelect(item)}
                                    className="w-full text-left px-4 py-3 hover:bg-accent transition-colors border-b last:border-b-0"
                                >
                                    {renderItem ? (
                                        renderItem(item)
                                    ) : (
                                        <div className="space-y-1">
                                            <div className="font-medium">{primary}</div>
                                            {secondary && (
                                                <div className="text-sm text-muted-foreground line-clamp-2">
                                                    {secondary}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}

                {showDropdown && suggestions.length === 0 && !isLoading && searchQuery.trim().length >= minChars && (
                    <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg px-4 py-3 text-sm text-muted-foreground">
                        No results found
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * AutoCompleteURL Renderer
 * Features:
 * - Debounced API search
 * - Minimum character threshold
 * - Custom data path extraction
 * - Custom item rendering support
 * - Loading state indication
 */
export const renderAutoCompleteURL: FieldRenderer = ({ field, value, handleChange, isRequired }) => {
    return (
        <AutoCompleteURLComponent
            field={field}
            value={value}
            handleChange={handleChange}
            isRequired={isRequired}
        />
    );
};

export default renderAutoCompleteURL;
